import { Injectable, PLATFORM_ID, inject, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subscription, catchError, interval, of, startWith, switchMap } from 'rxjs';
import { ClientService } from '../api/client.service';
import { Announcement } from '../api/modules/announcements';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService implements OnDestroy {
  private readonly client = inject(ClientService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _announcements = new BehaviorSubject<Announcement[]>([]);
  readonly announcements$: Observable<Announcement[]> = this._announcements.asObservable();

  private pollSub: Subscription | null = null;

  /** One-shot fetch on the same endpoint the poll loop uses; fails safe to `[]`. */
  fetchOnce(): Observable<Announcement[]> {
    return this.client.announcements.listActive().pipe(catchError(() => of([])));
  }

  /** Seeds the shared subject without a request, e.g. from an SSR `TransferState` value. */
  seed(list: Announcement[]): void {
    this._announcements.next(list);
  }

  /**
   * `skipInitialFetch` avoids an immediate duplicate request right after hydration when the
   * caller already seeded fresh data (e.g. from SSR TransferState).
   */
  startPolling(options: { skipInitialFetch?: boolean } = {}): void {
    if (!isPlatformBrowser(this.platformId) || this.pollSub) return;

    const ticks$ = options.skipInitialFetch ? interval(60_000) : interval(60_000).pipe(startWith(0));

    this.pollSub = ticks$
      .pipe(switchMap(() => this.fetchOnce()))
      .subscribe(list => this._announcements.next(list));
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }
}
